/** Login / Register split-screen. Ported from `admin-auth.jsx` `AuthScreen`, wired to the real API. */
import { useRef, useState } from 'react';
import { HerosMark } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { apiClient, ApiClientError } from '../lib/apiClient';
import type { ForgotPasswordResponse } from '@heros/shared';

export function AuthScreen({ onSignIn }: { onSignIn?: () => void }) {
  void onSignIn; // App passes a demo affordance; AuthScreen drives auth itself.
  const { login, register, loading } = useAuth();
  // Open straight to "Request access" when the shared invite link (?register=1) is used.
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('register') === '1'
      ? 'register'
      : 'login',
  );

  // While the bootstrap GET /api/auth/me is in flight, show a neutral splash
  // instead of flashing the sign-in form (App's gate renders us when user==null).
  if (loading) {
    return (
      <div className="auth-shell">
        <div className="auth-hero" />
        <div className="auth-form" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: 'var(--text-2)' }}>
            <HerosMark size={40} />
            <div style={{ fontSize: 13 }}>Checking your session…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HerosMark size={48} />
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.01em' }}>HEROs</div>
            <div style={{ fontSize: 11.5, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Health Evidence and Research Operations</div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 14, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Internal research management system</div>
          <h1 style={{ fontSize: 40, lineHeight: 1.15, fontWeight: 700, letterSpacing: '-0.02em', margin: 0, maxWidth: 540 }}>
            Plan trials. Track teams. Build quotations. <span style={{ color: 'var(--accent-soft)' }}>One operations hub.</span>
          </h1>
          <div style={{ fontSize: 14, opacity: 0.7, marginTop: 14, maxWidth: 480, lineHeight: 1.55 }}>
            See who is doing what each month across every project. From initial design through analysis to summary report — all in one place.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, fontSize: 11.5, opacity: 0.6 }}>
          <span>BioCE · Faculty of Medicine</span>
          <span>Chiang Mai University</span>
          <span>v0.4.0 MVP preview</span>
        </div>
      </div>

      <div className="auth-form">
        {mode === 'login'
          ? <LoginForm login={login} toRegister={() => setMode('register')} toForgot={() => setMode('forgot')} />
          : mode === 'register'
            ? <RegisterForm register={register} toLogin={() => setMode('login')} />
            : <ForgotPasswordForm toLogin={() => setMode('login')} />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Login
 * ------------------------------------------------------------------ */

function LoginForm({ login, toRegister, toForgot }: { login: (e?: string, p?: string, remember?: boolean) => Promise<void>; toRegister: () => void; toForgot: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password, remember);
      // On success the auth gate swaps to the app shell; nothing else to do.
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isRateLimited) {
          setError(err.body?.message ?? 'Too many attempts. Your account is temporarily locked — please try again shortly.');
        } else if (err.isAuth) {
          setError('Invalid email or password.');
        } else if (err.status === 0) {
          setError('Cannot reach the server. Check your connection and try again.');
        } else {
          setError(err.body?.message ?? 'Sign-in failed. Please try again.');
        }
      } else {
        setError('Sign-in failed. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h2>Sign in</h2>
      <div className="sub">Access is restricted to approved team members.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="field">
          <label>Gmail address</label>
          <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} />
        </div>
        <div className="field">
          <label>Password</label>
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={submitting} />
        </div>
        {error && (
          <div role="alert" style={{ padding: '8px 10px', background: 'rgba(158,43,37,0.08)', border: '1px solid rgba(158,43,37,0.35)', color: 'var(--accent)', borderRadius: 6, fontSize: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, color: 'var(--text-2)' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={submitting} /> Trust this device for 30 days
          </label>
          <a style={{ fontSize: 11.5, color: 'var(--navy)', textDecoration: 'none', cursor: 'pointer' }} onClick={toForgot}>Forgot password?</a>
        </div>
        <button type="submit" className="btn primary" style={{ height: 36, justifyContent: 'center', fontWeight: 600 }} disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--text-2)', textAlign: 'center', marginTop: 6 }}>
          Don't have an account? <a style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }} onClick={toRegister}>Request access</a>
        </div>
        <div style={{ padding: 8, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11, color: 'var(--text-2)', textAlign: 'center', marginTop: 6 }}>
          🔒 New accounts require admin verification &amp; role assignment before activation.
        </div>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Forgot password (admin-triaged reset request; deliberately generic)
 * ------------------------------------------------------------------ */

function ForgotPasswordForm({ toLogin }: { toLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<ForgotPasswordResponse | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!email.trim()) { setError('Please enter your account email.'); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.auth.forgotPassword(email.trim());
      setDone(res);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isRateLimited) setError(err.body?.message ?? 'Too many requests. Please try again shortly.');
        else if (err.status === 0) setError('Cannot reach the server. Check your connection and try again.');
        else setError(err.body?.message ?? 'Could not submit your request. Please try again.');
      } else {
        setError('Could not submit your request. Please try again.');
      }
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, padding: '24px 8px' }}>
        <div style={{ width: 48, height: 48, borderRadius: 50, background: 'rgba(46,106,60,0.12)', color: '#2E6A3C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>✓</div>
        <h2 style={{ margin: 0 }}>Request sent</h2>
        <div className="sub" style={{ maxWidth: 360 }}>{done.message}</div>
        <div style={{ padding: 10, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 360 }}>
          For your security we don't confirm whether an account exists. If it does, an administrator will contact you with a temporary password.
        </div>
        <button className="btn" onClick={toLogin}>Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <h2>Reset your password</h2>
      <div className="sub">Enter your account email. The administrators will be notified and will contact you with a temporary password.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="field">
          <label>Gmail address</label>
          <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} placeholder="you@gmail.com" />
        </div>
        {error && (
          <div role="alert" style={{ padding: '8px 10px', background: 'rgba(158,43,37,0.08)', border: '1px solid rgba(158,43,37,0.35)', color: 'var(--accent)', borderRadius: 6, fontSize: 12 }}>
            {error}
          </div>
        )}
        <button type="submit" className="btn primary" style={{ height: 36, justifyContent: 'center', fontWeight: 600 }} disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset request'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--text-2)', textAlign: 'center', marginTop: 6 }}>
          Remembered it? <a style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }} onClick={toLogin}>Back to sign in</a>
        </div>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Register (multipart: fields + cv/gcp/hsp files)
 * ------------------------------------------------------------------ */

import type { RegisterFields, RegisterFiles, RegisterResponse } from '../lib/apiClient';

function RegisterForm({
  register,
  toLogin,
}: {
  register: (fields: RegisterFields, files?: RegisterFiles) => Promise<RegisterResponse>;
  toLogin: () => void;
}) {
  const [form, setForm] = useState({
    name: '', email: '', password: '', affiliation: '', position: '', phone: '', expertise: '',
  });
  const [files, setFiles] = useState<RegisterFiles>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<RegisterResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // The three documents (cv/gcp/hsp) come from a single multi-file picker; we
  // map the first three selected files onto the named slots in order.
  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (!list || list.length === 0) { setFiles({}); return; }
    const arr = Array.from(list);
    setFiles({ cv: arr[0] ?? null, gcp: arr[1] ?? null, hsp: arr[2] ?? null });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!form.name || !form.email || !form.password || !form.affiliation || !form.position) {
      setError('Please complete name, email, password, affiliation and position.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await register(
        {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          affiliation: form.affiliation.trim(),
          position: form.position.trim(),
          // Role is assigned by an admin on approval; send a neutral request label.
          role: 'Pending assignment',
          phone: form.phone.trim() || undefined,
          expertise: form.expertise.trim() || undefined,
        },
        files,
      );
      setDone(res);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isRateLimited) setError(err.body?.message ?? 'Too many requests. Please try again shortly.');
        else if (err.status === 0) setError('Cannot reach the server. Check your connection and try again.');
        else setError(err.body?.message ?? 'Could not submit your request. Please review your details.');
      } else {
        setError('Could not submit your request. Please try again.');
      }
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, padding: '24px 8px' }}>
        <div style={{ width: 48, height: 48, borderRadius: 50, background: 'rgba(46,106,60,0.12)', color: '#2E6A3C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>✓</div>
        <h2 style={{ margin: 0 }}>Request submitted</h2>
        <div className="sub" style={{ maxWidth: 360 }}>{done.message}</div>
        <div style={{ padding: 10, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 360 }}>
          An administrator will verify your identity and assign your role before your account is activated. You will be able to sign in once approved.
        </div>
        <button className="btn" onClick={toLogin}>Back to sign in</button>
      </div>
    );
  }

  const fileNames = [files.cv, files.gcp, files.hsp].filter(Boolean) as File[];

  return (
    <form onSubmit={submit}>
      <h2>Request access</h2>
      <div className="sub">Submit your details. An admin will verify your identity and assign your role before activation.</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div className="field"><label>Full name</label><input placeholder="Dr. Jane Doe" value={form.name} onChange={set('name')} disabled={submitting} /></div>
        <div className="field"><label>Gmail address</label><input type="email" placeholder="jane.doe@gmail.com" value={form.email} onChange={set('email')} disabled={submitting} /></div>
        <div className="field"><label>Password</label><input type="password" placeholder="At least 8 characters" value={form.password} onChange={set('password')} disabled={submitting} /></div>
        <div className="field"><label>Affiliation</label><input placeholder="Faculty / Dept" value={form.affiliation} onChange={set('affiliation')} disabled={submitting} /></div>
        <div className="field"><label>Position</label><input placeholder="Resident, RA, etc." value={form.position} onChange={set('position')} disabled={submitting} /></div>
        <div className="field"><label>Phone</label><input placeholder="+66 …" value={form.phone} onChange={set('phone')} disabled={submitting} /></div>
        <div className="field" style={{ gridColumn: 'span 2' }}><label>Area of expertise</label><input placeholder="e.g. Cardiology, biostatistics" value={form.expertise} onChange={set('expertise')} disabled={submitting} /></div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Upload CV, GCP &amp; HSP certificates (select up to 3, in that order)</label>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,application/pdf,image/png,image/jpeg" style={{ display: 'none' }} onChange={onFiles} disabled={submitting} />
          <div
            onClick={() => !submitting && fileInputRef.current?.click()}
            style={{ padding: 18, border: '2px dashed var(--border-strong)', borderRadius: 6, textAlign: 'center', fontSize: 12, color: 'var(--text-2)', cursor: submitting ? 'default' : 'pointer' }}
          >
            {fileNames.length === 0
              ? <>Drop files here, or <span style={{ color: 'var(--accent)' }}>browse</span></>
              : <span>{fileNames.map((f) => f.name).join(', ')}</span>}
          </div>
        </div>
        {error && (
          <div role="alert" style={{ gridColumn: 'span 2', padding: '8px 10px', background: 'rgba(158,43,37,0.08)', border: '1px solid rgba(158,43,37,0.35)', color: 'var(--accent)', borderRadius: 6, fontSize: 12 }}>
            {error}
          </div>
        )}
        <div className="field" style={{ gridColumn: 'span 2', padding: 10, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
          ℹ Role is <strong>not</strong> selected at registration. After review, an admin will assign one of: Project Manager, PI, Statistician, Data Manager, Research Assistant, Monitor, Finance, or Viewer.
        </div>
        <button type="submit" className="btn primary" style={{ gridColumn: 'span 2', height: 36, justifyContent: 'center' }} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
        <div style={{ fontSize: 11.5, color: 'var(--text-2)', gridColumn: 'span 2', textAlign: 'center' }}>
          Already approved? <a style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={toLogin}>Sign in</a>
        </div>
      </div>
    </form>
  );
}
